import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';

/**
 * @typedef {Object} GiftCardRecipientFormRefs
 * @property {HTMLDivElement} recipientFields - Container for recipient form fields
 * @property {HTMLInputElement} senderEmail - Sender email input field
 * @property {HTMLInputElement} recipientEmail - Recipient email input field
 * @property {HTMLInputElement} recipientName - Recipient name input field
 * @property {HTMLTextAreaElement} recipientMessage - Recipient message textarea
 * @property {HTMLInputElement} recipientSendOn - Send on date input
 * @property {HTMLButtonElement} submitButton - Submit button
 * @property {HTMLInputElement} [timezoneOffset] - Timezone offset hidden input (optional)
 * @property {HTMLDivElement} [emailError] - Email error message container (optional)
 * @property {HTMLDivElement} [nameError] - Name error message container (optional)
 * @property {HTMLDivElement} [messageError] - Message error message container (optional)
 * @property {HTMLDivElement} [sendOnError] - Send on error message container (optional)
 * @property {HTMLSpanElement} [characterCount] - Character count display element (optional)
 * @property {HTMLDivElement} [liveRegion] - Live region for screen reader announcements (optional)
 */

/**
 * @extends {Component<GiftCardRecipientFormRefs>}
 */
class GiftCardRecipientForm extends Component {
  #isEnabled = false;
  /** @type {(() => void) | null} */
  #updateCharacterCountBound = null;
  /** @type {((event: Event) => void) | null} */
  #displayCartErrorBound = null;
  /** @type {((event: Event) => void) | null} */
  #cartUpdateBound = null;
  /** @type {(() => void) | null} */
  #handleDialogOpenBound = null;
  /** @type {(() => void) | null} */
  #handleDialogCloseBound = null;
  /** @type {(() => void) | null} */
  #handleSubmitBound = null;

  requiredRefs = ['recipientFields', 'senderEmail', 'recipientEmail', 'recipientName', 'recipientMessage', 'recipientSendOn', 'submitButton'];

  get #inputFields() {
    return [this.refs.senderEmail, this.refs.recipientEmail, this.refs.recipientName, this.refs.recipientMessage, this.refs.recipientSendOn];
  }

  get #dialogComponent() {
    return this.closest('dialog-component');
  }

  get #associatedForm() {
    const formId = this.refs.submitButton.getAttribute('form');
    return formId ? document.getElementById(formId) : null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#initializeForm();

    this.#updateCharacterCountBound = () => this.#updateCharacterCount();
    this.refs.recipientMessage.addEventListener('input', this.#updateCharacterCountBound);

    this.#displayCartErrorBound = this.#displayCartError.bind(this);
    document.addEventListener(ThemeEvents.cartError, this.#displayCartErrorBound);

    this.#cartUpdateBound = this.#handleCartUpdate.bind(this);
    document.addEventListener(ThemeEvents.cartUpdate, this.#cartUpdateBound);

    this.#handleDialogOpenBound = () => this.#enableRecipientFields();
    this.#dialogComponent?.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpenBound);

    this.#handleDialogCloseBound = () => this.#disableAndClearFields();
    this.#dialogComponent?.addEventListener(DialogCloseEvent.eventName, this.#handleDialogCloseBound);

    this.#handleSubmitBound = () => this.#setSubmitting();
    this.refs.submitButton.addEventListener('click', this.#handleSubmitBound);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#updateCharacterCountBound) {
      this.refs.recipientMessage.removeEventListener('input', this.#updateCharacterCountBound);
      this.#updateCharacterCountBound = null;
    }

    if (this.#displayCartErrorBound) {
      document.removeEventListener(ThemeEvents.cartError, this.#displayCartErrorBound);
      this.#displayCartErrorBound = null;
    }

    if (this.#cartUpdateBound) {
      document.removeEventListener(ThemeEvents.cartUpdate, this.#cartUpdateBound);
      this.#cartUpdateBound = null;
    }

    if (this.#handleDialogOpenBound) {
      this.#dialogComponent?.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpenBound);
      this.#handleDialogOpenBound = null;
    }

    if (this.#handleDialogCloseBound) {
      this.#dialogComponent?.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogCloseBound);
      this.#handleDialogCloseBound = null;
    }

    if (this.#handleSubmitBound) {
      this.refs.submitButton.removeEventListener('click', this.#handleSubmitBound);
      this.#handleSubmitBound = null;
    }
  }

  #initializeForm() {
    this.#setDateConstraints();
    this.#updateCharacterCount();
  }

  #enableRecipientFields() {
    if (this.#isEnabled) return;
    this.#isEnabled = true;

    for (const field of this.#inputFields) {
      field.disabled = false;
      if (field === this.refs.recipientEmail) {
        field.setAttribute('required', 'required');
      }
    }

    if (this.refs.timezoneOffset) {
      this.refs.timezoneOffset.disabled = false;
      this.refs.timezoneOffset.value = new Date().getTimezoneOffset().toString();
    }

    this.#addControlField();
  }

  #disableAndClearFields() {
    this.#isEnabled = false;

    for (const field of this.#inputFields) {
      field.disabled = true;
      field.value = '';
      field.removeAttribute('required');
    }

    if (this.refs.timezoneOffset) {
      this.refs.timezoneOffset.disabled = true;
      this.refs.timezoneOffset.value = '';
    }

    this.#removeControlField();
    this.#clearErrorMessages();
    this.#updateCharacterCount();
  }

  #addControlField() {
    const existingControl = this.querySelector('input[name="properties[__shopify_send_gift_card_to_recipient]"]');
    if (existingControl) return;

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'properties[__shopify_send_gift_card_to_recipient]';
    input.value = 'on';
    this.prepend(input);
  }

  #removeControlField() {
    const controlField = this.querySelector('input[name="properties[__shopify_send_gift_card_to_recipient]"]');
    controlField?.remove();
  }

  #setSubmitting() {
    const form = this.#associatedForm;
    if (form && !form.checkValidity()) return;

    const { submitButton } = this.refs;
    const submittingText = submitButton.dataset.submittingText;
    if (submittingText) {
      submitButton.textContent = submittingText;
      submitButton.style.pointerEvents = 'none';
    }
  }

  #resetSubmitButton() {
    const { submitButton } = this.refs;
    const submitText = submitButton.dataset.submitText;
    if (submitText) {
      submitButton.textContent = submitText;
      submitButton.style.pointerEvents = '';
    }
  }

  #handleCartUpdate(event) {
    this.#resetSubmitButton();

    if (event.detail?.data?.didError) return;

    this.#clearErrorMessages();
    this.#dialogComponent?.closeDialog();
  }

  /**
   * Update character count display
   */
  #updateCharacterCount() {
    if (!this.refs.characterCount) return;

    const currentLength = this.refs.recipientMessage.value.length;
    const maxLength = this.refs.recipientMessage.maxLength;

    const template = this.refs.characterCount.getAttribute('data-template');
    if (!template) return;

    const updatedText = template.replace('[current]', currentLength.toString()).replace('[max]', maxLength.toString());

    this.refs.characterCount.textContent = updatedText;
  }

  /**
   * Set date constraints for the send on date picker
   * Prevents selecting past dates and limits to 90 days in the future
   */
  #setDateConstraints() {
    const today = new Date();
    const maxDate = new Date();
    maxDate.setDate(today.getDate() + 90);

    // Format dates as YYYY-MM-DD
    /**
     * @param {Date} date
     * @returns {string}
     */
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    this.refs.recipientSendOn.setAttribute('min', formatDate(today));
    this.refs.recipientSendOn.setAttribute('max', formatDate(maxDate));
  }

  /**
   * Handles cart error events
   * @param {CartErrorEvent} event - The cart error event
   */
  #displayCartError(event) {
    this.#resetSubmitButton();

    if (event.detail?.data) {
      const { message, errors, description } = event.detail.data;

      // Display the error message
      if (errors && typeof errors === 'object') {
        this.#displayErrorMessage(message || 'There was an error', errors);
      } else if (message) {
        this.#displayErrorMessage(message, description);
      }
    }
  }

  /**
   * Display error messages in the appropriate error containers
   * @param {string} title - The main error message title
   * @param {Object} body - Error details
   */
  #displayErrorMessage(title, body) {
    this.#clearErrorMessages();

    if (typeof body === 'object' && body !== null) {
      /** @type {Record<string, {inputRef: string, errorRef: string}>} */
      const fieldMap = {
        email: { inputRef: 'recipientEmail', errorRef: 'emailError' },
        name: { inputRef: 'recipientName', errorRef: 'nameError' },
        message: { inputRef: 'recipientMessage', errorRef: 'messageError' },
        send_on: { inputRef: 'recipientSendOn', errorRef: 'sendOnError' },
      };

      for (const [field, errorMessages] of Object.entries(body)) {
        const fieldConfig = fieldMap[field];
        if (!fieldConfig) continue;

        const { inputRef, errorRef } = fieldConfig;
        const errorContainer = this.refs[errorRef];
        const inputElement = this.refs[inputRef];

        if (errorContainer && errorContainer instanceof HTMLElement) {
          const errorTextElement = errorContainer.querySelector('span');
          if (errorTextElement) {
            const message = Array.isArray(errorMessages) ? errorMessages.join(', ') : errorMessages;
            errorTextElement.textContent = `${message}.`;
          }

          errorContainer.classList.remove('hidden');
        }

        if (inputElement && inputElement instanceof HTMLElement) {
          // Set ARIA attributes for accessibility
          inputElement.setAttribute('aria-invalid', 'true');
          const errorId = `RecipientForm-${field}-error-${this.dataset.sectionId || 'default'}`;
          inputElement.setAttribute('aria-describedby', errorId);
        }
      }
    }

    // Announce errors to screen readers
    if (this.refs.liveRegion) {
      this.refs.liveRegion.textContent =
        title || Theme.translations?.recipient_form_error || 'There was an error with the form submission';
    }
  }

  /**
   * Clear all error messages and reset ARIA attributes
   */
  #clearErrorMessages() {
    // List of error container refs
    const errorRefs = ['emailError', 'nameError', 'messageError', 'sendOnError'];

    for (const errorRef of errorRefs) {
      const errorContainer = this.refs[errorRef];
      if (errorContainer && errorContainer instanceof HTMLElement) {
        errorContainer.classList.add('hidden');
        const errorTextElement = errorContainer.querySelector('span');
        if (errorTextElement) {
          errorTextElement.textContent = '';
        }
      }
    }

    // Remove ARIA attributes from all input fields
    for (const field of this.#inputFields) {
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-describedby');
    }

    // Clear live region announcement
    if (this.refs.liveRegion) {
      this.refs.liveRegion.textContent = '';
    }
  }

}

// Register the custom element
customElements.define('gift-card-recipient-form', GiftCardRecipientForm);
